import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import { useNeighbours } from "../LandingPageContext";
import {
  LandingImage,
  LandingSection,
  type SectionPadding,
} from "../LandingPrimitives";
import { BandHeading, type Cards } from "./cardsShared";
import { BENEFITS_SECTION_FOOT, SHALLOW_FOOT } from "./landingBands";
import {
  CONTAIN_GAP,
  FLOW_MEASURE,
  flowSection,
  sectionPad,
} from "./landingRun";
import { RUN_RAIL, continuesRun, opensOnNothing, runFoot } from "./landingRun";
import { Lines } from "@/components/marketing/Lines";

/**
 * `benefits_item`: the tinted card the published `benefits_section` is built
 * from. The lilac wash turns to the flat surface at the card step, and the box
 * closes up a size below it.
 */
const BENEFIT_CARD = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  p: "24px",
  borderRadius: "16px",
  border: `1px solid ${marketingPalette.plainCardRule}`,
  backgroundColor: marketingPalette.plainCard,
  [MID]: { backgroundColor: marketingPalette.surface },
  [TINY]: { p: "16px", gap: "16px" },
} satisfies SxProps<Theme>;

/**
 * `.ppc-why_list`: the cards wrapped and centred rather than gridded, each
 * 400 to 420 wide, 300 to 320 below the wide step, a column from the card step.
 */
const WHY_LIST = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "stretch",
  gap: "20px",
  "& > *": { minWidth: "400px", maxWidth: "420px" },
  [NOT_WIDE]: { "& > *": { minWidth: "300px", maxWidth: "320px" } },
  [MID]: {
    flexDirection: "column",
    gap: "16px",
    "& > *": { minWidth: "auto", maxWidth: "none" },
  },
} satisfies SxProps<Theme>;

/** `.benefits_body`: a point's line, 14 on 160% at 70%. */
const POINT = {
  fontSize: 14,
  lineHeight: "22.4px",
  opacity: 0.7,
} satisfies SxProps<Theme>;

/** `benefits_title`, and the tighter step the use-case grid sets it in. */
function titleSx(tight?: true): SxProps<Theme> {
  return tight === true
    ? {
        color: marketingPalette.ink,
        fontSize: 18.08,
        fontWeight: 500,
        // The published title is an `h6` in a rich-text block, on the h6's own 24.
        lineHeight: "24px",
        letterSpacing: "-0.3616px",
      }
    : {
        color: marketingPalette.ink,
        fontSize: 18,
        fontWeight: 500,
        lineHeight: "27px",
      };
}

/** `benefits_bottom`, published only where the card carries a line or a list; the title alone otherwise. */
function BenefitCopy({ card, tight }: { card: Card; tight?: true }) {
  // `.benefits_title` is a plain block; only the use-case grid's rich text
  // publishes its titles as `h6`.
  const title = (
    <Typography component={tight ? "h6" : "div"} sx={titleSx(tight)}>
      <Lines text={card.title} />
    </Typography>
  );
  if (card.points?.length) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {title}
        {/* `.pp-dot-list`: the points dotted, 23 in. As published, the
            first point's `li` carries `.benefits_body` itself, so its row
            and its dot are the line's 14 at 70%; the rest hold it inside. */}
        <Box component="ul" sx={{ m: 0, mb: "10px", pl: "23px" }}>
          {card.points.map((point, index) => (
            <Box
              component="li"
              key={point}
              // The site's every `li` closes on .3rem.
              sx={[
                { mb: "4.8px", color: marketingPalette.ink },
                index === 0 ? POINT : {},
              ]}
            >
              <Box sx={index === 0 ? undefined : POINT}>{point}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
  if (!card.body) {
    return title;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {title}
      <Typography
        sx={{
          // `.benefits_body` is 14 on 160% in the page's ink; the use-case
          // grid's rich text sets its lines at .88rem, which wraps a step
          // sooner, in `--colors--gray-1`.
          color: tight ? marketingPalette.inkMuted : marketingPalette.ink,
          fontSize: tight ? 14.08 : 14,
          lineHeight: tight ? "22.528px" : "22.4px",
          opacity: 0.7,
        }}
      >
        {card.body}
      </Typography>
    </Box>
  );
}

function BenefitCard({
  card,
  tight,
  centred,
}: {
  card: Card;
  tight?: true;
  /** `is-centered-txt`: the picture cards, which centre their copy. */
  centred?: true;
}) {
  return (
    <Box sx={[BENEFIT_CARD, centred === true ? { textAlign: "center" } : {}]}>
      {card.icon ? (
        <LandingImage
          src={card.icon}
          sx={{
            width: "48px",
            height: "48px",
            objectFit: "cover",
            [TINY]: { width: "32px", height: "32px" },
          }}
        />
      ) : null}
      {card.image ? (
        <LandingImage src={card.image} sx={{ width: 1, height: "auto" }} />
      ) : null}
      <BenefitCopy card={card} tight={tight} />
    </Box>
  );
}

/**
 * The band's own breaks. `benefits_wrap` carries the section break ABOVE
 * itself as a margin, which only shows where the block before it carries
 * none: under a hero, which closes on its box. As a step of a run (the pair
 * of tiles the comparison pages set under their trust band) it closes on the
 * run's gutter instead of the break, and over a section that opens on nothing
 * it closes on `.container_new.padding-bottom`.
 */
function useBenefitsSection(block: Cards, continues: boolean) {
  const { previous, next } = useNeighbours(block);
  const foot = runFoot(next);
  if (block.contain) {
    return flowSection(block);
  }
  if (continues || foot !== undefined) {
    return { padding: "none" as const, sx: foot };
  }
  const section = benefitsBreak(block, previous, next);
  return closesOwnSection(block) ? withSectionFoot(section) : section;
}

/**
 * Whether the band is a published `.benefits_section` of its own, closing on
 * it. The paid-search why list is another component, a grid with no heading
 * of its own is a step of the section above (the alternative pages'
 * `.alter-features_list`), and a band that names only its top shares its
 * section with the block after it, which carries the close.
 */
function closesOwnSection(block: Cards): boolean {
  return !continuesRun(block) && !block.whyList && block.pad !== "top";
}

/** The three paddings named for the band already carry the section's 20. */
const CARRIES_FOOT: ReadonlySet<SectionPadding> = new Set([
  "benefits",
  "benefitsBreak",
  "bothTail",
]);

function withSectionFoot(section: {
  padding: SectionPadding;
  sx?: SxProps<Theme>;
}) {
  if (CARRIES_FOOT.has(section.padding)) {
    return section;
  }
  const own = Array.isArray(section.sx) ? section.sx : [section.sx];
  return { ...section, sx: [...own, BENEFITS_SECTION_FOOT] };
}

/** The band's own break, where it is no step of a run. */
function benefitsBreak(
  block: Cards,
  previous: ReturnType<typeof useNeighbours>["previous"],
  next: ReturnType<typeof useNeighbours>["next"]
) {
  if (block.pad) {
    // Under a hero the band still opens on its own 100: the page names only its close.
    const underHero = previous?.kind === "hero" && block.pad === "break";
    return {
      padding: underHero ? ("topBreak" as const) : sectionPad(block.pad),
    };
  }
  if (opensOnNothing(next)) {
    return { padding: "none" as const, sx: SHALLOW_FOOT };
  }
  return {
    padding:
      previous?.kind === "hero" ? ("both" as const) : ("bottom" as const),
  };
}

/**
 * `benefits_wrap`: a centred heading over a three-up grid of tinted cards. Two
 * card shapes share it, an icon over a title and a line, and a picture over a
 * centred title, and the published page draws both in the same box.
 */
export default function CardsBenefits({
  block,
  continues,
}: {
  block: Cards;
  continues: boolean;
}) {
  const columns = Math.min(block.items.length, block.across ?? 3);
  const centred = block.items.some((card) => card.image) ? true : undefined;
  return (
    <LandingSection {...useBenefitsSection(block, continues)}>
      <Box
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            gap: "40px",
            [MID]: { gap: "32px" },
            [TINY]: { gap: "24px" },
          },
          continuesRun(block) ? RUN_RAIL : null,
          block.contain ? [FLOW_MEASURE, CONTAIN_GAP] : null,
        ].flat()}
      >
        <BandHeading block={block} gap="8px" />
        <Box
          sx={[
            block.whyList
              ? WHY_LIST
              : {
                  display: "grid",
                  gap: "20px",
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                  // `.benefits_list` names its rows below the wide step, two then
                  // four, so a short list leaves an empty row and the gap before it.
                  // `.alter-features_list` names two at both steps.
                  [NOT_WIDE]: {
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gridTemplateRows: "repeat(2, auto)",
                  },
                  [MID]: {
                    gridTemplateColumns: block.holdPairs
                      ? "repeat(2, minmax(0, 1fr))"
                      : "1fr",
                    gridTemplateRows: `repeat(${block.pairList ? 2 : 4}, auto)`,
                    gap: "16px",
                  },
                },
            block.holdPairs ? { [TINY]: { gridTemplateColumns: "1fr" } } : null,
          ]}
        >
          {block.items.map((card, index) => (
            <BenefitCard
              key={index}
              card={card}
              tight={block.tightCardTitle}
              centred={centred}
            />
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}
